import React, { useState } from 'react'
import { api, formatDate } from '../../utils/api'
import CopyButton from '../shared/CopyButton.jsx'

/**
 * Read-only viewer for a customer's email history, keyed on their address.
 *
 * The address is a toggle; expanding it fetches the history once and lists it,
 * and a row opens the full message. A copy icon sits beside the address.
 * Shared by the deal detail modal and the
 * collections detail panel so both read one implementation.
 */
export default function CustomerEmails({ email }) {
  const [expanded, setExpanded] = useState(false)
  const [emails, setEmails] = useState(null) // null = not loaded yet
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [detail, setDetail] = useState(null)
  const [detailError, setDetailError] = useState(false)

  async function loadList() {
    setLoading(true)
    setLoadError(false)
    try {
      const response = await api.get(`/api/emails?email=${encodeURIComponent(email)}`)
      setEmails(response.emails || [])
    } catch (error) {
      console.error('Failed to load customer emails:', error)
      // Leave emails null so the fetch is retryable rather than stuck on
      // "No emails found" after a transient failure.
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }

  function toggle() {
    if (expanded) {
      setExpanded(false)
      return
    }
    setExpanded(true)
    if (emails === null && !loading) loadList()
  }

  async function openDetail(messageId, source) {
    setDetailError(false)
    try {
      const response = await api.get(`/api/emails/${messageId}?source=${source}`)
      if (response.email) setDetail(response.email)
    } catch (error) {
      console.error('Failed to load email:', error)
      setDetailError(true)
    }
  }

  if (!email) return null

  return (
    <div>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <button
          type="button"
          className={`email-link-btn ${expanded ? 'active' : ''}`}
          onClick={toggle}
        >
          {email}
          <span style={{ marginLeft: 4, fontSize: '0.7rem' }}>{expanded ? '▲' : '▼'}</span>
        </button>
        <CopyButton value={email} label="Copy email address" toastMessage="Email address copied" />
      </div>

      {expanded && (
        <div className="email-list-dropdown">
          {detailError && (
            <div style={{ padding: 8, textAlign: 'center', fontSize: '0.8rem', color: 'var(--color-error)' }}>
              Couldn't open that message. Try again.
            </div>
          )}
          {loading ? (
            <div className="loading-spinner loading-spinner-sm" style={{ margin: '8px auto', display: 'block' }} />
          ) : loadError ? (
            <div style={{ padding: 8, textAlign: 'center', fontSize: '0.8rem' }}>
              <span style={{ color: 'var(--color-error)' }}>Couldn't load emails.</span>{' '}
              <button type="button" className="btn btn-xs" onClick={loadList} style={{ fontSize: 11 }}>Retry</button>
            </div>
          ) : emails && emails.length > 0 ? (
            <div className="email-list">
              {emails.map((item, idx) => (
                <div
                  key={item.messageId || idx}
                  className="email-list-item"
                  onClick={() => openDetail(item.messageId, item.source)}
                >
                  <div className="email-item-header">
                    <span className={`email-badge email-badge-${item.source}`}>
                      {item.source === 'postmark' ? 'Postmark' : item.source}
                    </span>
                    <span className={`email-direction email-direction-${item.direction}`}>
                      {item.direction === 'out' ? '→ Out' : '← In'}
                    </span>
                    {item.status && <span className="email-status">{item.status}</span>}
                    {item.openCount > 0 && <span className="email-opens">{item.openCount} opens</span>}
                    <span className="email-date">{formatDate(item.date)}</span>
                  </div>
                  <div className="email-subject">{item.subject}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-muted" style={{ padding: 8, textAlign: 'center', fontSize: '0.8rem' }}>
              No emails found
            </div>
          )}
        </div>
      )}

      {detail && <EmailDetailModal detail={detail} onClose={() => setDetail(null)} />}
    </div>
  )
}

function EmailDetailModal({ detail, onClose }) {
  return (
    <div className="modal-overlay" style={{ zIndex: 1200 }} onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 700, maxHeight: '80vh' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>{detail.subject}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body-scroll" style={{ padding: 'var(--space-4)' }}>
          <div style={{ marginBottom: 'var(--space-3)', fontSize: '0.85rem' }}>
            <div><strong>From:</strong> {detail.from}</div>
            <div><strong>To:</strong> {detail.to}</div>
            <div><strong>Date:</strong> {formatDate(detail.date)}</div>
            <div><strong>Source:</strong> {detail.source}</div>
          </div>
          <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 'var(--space-3)' }}>
            {detail.htmlBody ? (
              <iframe
                srcDoc={detail.htmlBody}
                style={{ width: '100%', minHeight: 400, border: '1px solid var(--color-border)', borderRadius: 4, background: 'white' }}
                sandbox="allow-same-origin"
                title="Email content"
              />
            ) : (
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.85rem', margin: 0 }}>
                {detail.textBody || 'No content'}
              </pre>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}
